import React from 'react';
import { ArrowRightIcon, CheckIcon, UsersIcon, BookOpenIcon, MicIcon, CalendarIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
interface ServiceLink {
  type: string;
  typeColor: string;
  title: string;
  desc: string;
  path: string;
  cta: string;
}
export function NewToLeadership() {
  const {
    t
  } = useLanguage();
  const signs = t('newToLeadership.signs') as string[];
  const whatWeDoItems = t('newToLeadership.whatWeDoItems') as string[];
  const serviceLinks: ServiceLink[] = [{
    type: t('newToLeadership.courseLabel') as string,
    typeColor: 'bg-coral/10 text-coral',
    title: t('newToLeadership.course1Title') as string,
    desc: t('newToLeadership.course1Desc') as string,
    path: '/services/courses/first-time-manager',
    cta: t('newToLeadership.learnMore') as string
  }, {
    type: t('newToLeadership.workshopLabel') as string,
    typeColor: 'bg-teal/10 text-teal',
    title: t('newToLeadership.workshop1Title') as string,
    desc: t('newToLeadership.workshop1Desc') as string,
    path: '/services/workshops',
    cta: t('newToLeadership.learnMore') as string
  }, {
    type: t('newToLeadership.talkLabel') as string,
    typeColor: 'bg-yellow-100 text-yellow-700',
    title: t('newToLeadership.talk1Title') as string,
    desc: t('newToLeadership.talk1Desc') as string,
    path: '/services/talks',
    cta: t('newToLeadership.learnMore') as string
  }];
  return <PageTransition data-id="element-1600">
      <div className="pt-12 pb-24" data-id="element-1601">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1602">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1603" />
          <Animate className="max-w-3xl" data-id="element-1604">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1605">
              {t('newToLeadership.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1606">
              {t('newToLeadership.title')}
              <br data-id="element-1607" />
              <span className="text-coral" data-id="element-1608">{t('newToLeadership.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1609">
              {t('newToLeadership.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1610">
              {t('newToLeadership.intro2')}
            </p>
            <Link to="/contact" data-id="element-1611">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1613" />} data-id="element-1612">
                {t('newToLeadership.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs section */}
        <section className="bg-cream py-20 mb-20" data-id="element-1614">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1615">
            <Animate className="mb-12" data-id="element-1616">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1617">
                {t('newToLeadership.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1618">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1619">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1620">
                    <div className="mt-0.5 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1621">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1622" />
                    </div>
                    <span className="text-gray-700" data-id="element-1623">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What we work on */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1624">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1625">
            <Animate data-id="element-1626">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1627">
                {t('newToLeadership.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1628">
                {t('newToLeadership.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1629">
                {t('newToLeadership.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1630">
              <div className="space-y-3" data-id="element-1631">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1632">
                    <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-1633">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1634" />
                    </div>
                    <span className="text-gray-700" data-id="element-1635">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* Ways to work together */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1636">
          <Animate className="mb-12" data-id="element-1637">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-1638">
              {t('newToLeadership.waysTitle')}
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-1639">{t('newToLeadership.waysSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1640">
            {serviceLinks.map((link, i) => {
            const icons = [<BookOpenIcon size={24} className="text-coral" data-id="element-1641" />, <LayersIcon size={24} className="text-teal" data-id="element-1642" />, <MicIcon size={24} className="text-yellow-600" data-id="element-1643" />];
            return <Animate key={i} delay={i * 0.1} data-id="element-1644">
                  <Card hoverEffect className="h-full p-6 flex flex-col" data-id="element-1645">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1646">
                      {icons[i]}
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${link.typeColor}`} data-id="element-1647">
                        {link.type}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg text-charcoal mb-2" data-id="element-1648">{link.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1649">{link.desc}</p>
                    <Link to={link.path} data-id="element-1650">
                      <Button variant="outline" size="sm" rightIcon={<ArrowRightIcon size={16} data-id="element-1652" />} data-id="element-1651">
                        {link.cta}
                      </Button>
                    </Link>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1653">
          <Animate data-id="element-1654">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-1655">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1656">
                <CalendarIcon size={32} className="text-coral" data-id="element-1657" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1658">{t('newToLeadership.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1659">{t('newToLeadership.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1660">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1662" />} data-id="element-1661">
                  {t('newToLeadership.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}